import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Divider,
  useToast,
  Input,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react';
import { FaPlus, FaChevronLeft, FaChevronRight, FaBolt, FaCheck, FaTimes, FaTrash, FaSearch } from 'react-icons/fa';
import { OlaggModule, PermissionEnum, TriviaCalendarResponse, TriviaQuestionTemplate } from '@olagg/db-types';
import { ROUTES } from '../../consts/routes';
import useRoles from '../../features/Roles/hooks/useRoles';
import useTrivia from './hooks/useTrivia';
import moment from 'moment';

const DAYS_OF_WEEK = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Tipo para las asignaciones del calendario (coincide con API real)
type CalendarAssignment = {
  id: string;
  template_id: string;
  title: string;
  position: number;
  points: number;
  active: boolean;
};

// Tipo para los datos del día (coincide con API real)
type DayCalendarData = {
  questions_count: number;
  total_points: number;
  has_speed_bonus: boolean;
  has_active_questions: boolean;
  assignments: CalendarAssignment[];
};

const TriviaCalendarView = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { missingPermission } = useRoles();
  const { getTriviaCalendar, getTriviaTemplates, createTriviaAssignment, deleteTriviaAssignment, loading } = useTrivia();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState<Record<string, DayCalendarData>>({});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedDayAssignments, setSelectedDayAssignments] = useState<CalendarAssignment[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // State para agregar templates
  const [templateSearch, setTemplateSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState<TriviaQuestionTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  const formatDateKey = (day: number): string => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const fetchCalendarData = async () => {
    try {
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
      const response: TriviaCalendarResponse = await getTriviaCalendar({ start_date: startDate, end_date: endDate });

      // La API ya devuelve los datos en el formato correcto
      if (response.calendar) {
        setCalendarData(response.calendar as Record<string, DayCalendarData>);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, [year, month]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleDayClick = async (day: number) => {
    const dateKey = formatDateKey(day);
    setSelectedDate(dateKey);
    setLoadingQuestions(true);

    try {
      // Usar los datos del calendario ya cargados
      const dayData = calendarData[dateKey];
      setSelectedDayAssignments(dayData?.assignments || []);
      onOpen();
    } catch (error) {
      console.error('Error fetching questions for day:', error);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleCreateQuestion = () => {
    if (selectedDate) {
      navigate(`${ROUTES.TRIVIA.create}?date=${selectedDate}`);
    } else {
      navigate(ROUTES.TRIVIA.create);
    }
  };

  const handleEditTemplate = (templateId: string) => {
    navigate(`/trivia/${templateId}`);
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (window.confirm('¿Estás seguro que deseas eliminar esta asignación? (El template no se eliminará)')) {
      setDeletingId(assignmentId);
      try {
        await deleteTriviaAssignment(assignmentId);
        toast({
          title: 'Asignación eliminada',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
        // Refrescar calendario y actualizar assignments del día
        await fetchCalendarData();
        // Actualizar localmente para evitar problemas de estado
        setSelectedDayAssignments(prev => prev.filter(a => a.id !== assignmentId));
      } catch (error: any) {
        toast({
          title: 'Error al eliminar',
          description: error?.message || 'No se pudo eliminar la asignación',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      } finally {
        setDeletingId(null);
      }
    }
  };

  // Buscar templates disponibles
  const fetchAvailableTemplates = async (search?: string) => {
    setLoadingTemplates(true);
    try {
      const response = await getTriviaTemplates({
        per_page: 5,
        search: search || undefined
      });
      setAvailableTemplates(response.triviaQuestionTemplates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Debounce del search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(templateSearch);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [templateSearch]);

  // Efecto para cargar templates cuando se abre el modal o cambia el search (debounced)
  useEffect(() => {
    if (isOpen) {
      fetchAvailableTemplates(debouncedSearch);
    }
  }, [isOpen, debouncedSearch]);

  // Asignar un template a la fecha seleccionada
  const handleAssignTemplate = async (templateUid: string, position?: number) => {
    if (!selectedDate) return;

    setAssigningId(templateUid);
    try {
      const finalPosition = position ?? selectedDayAssignments.length + 1;
      await createTriviaAssignment({
        assignment: {
          template_id: templateUid,
          date: selectedDate,
          position: finalPosition,
          active: true
        }
      });
      toast({
        title: 'Template asignado',
        description: `Template asignado al ${moment(selectedDate).format('DD/MM/YYYY')} en posición ${finalPosition}`,
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      // Refrescar calendario y datos del día
      await fetchCalendarData();
      const dayData = calendarData[selectedDate];
      setSelectedDayAssignments(dayData?.assignments || []);
      setSelectedPosition(null);
      // Recargar para obtener los datos actualizados
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error al asignar',
        description: error?.response?.data?.error || error?.message || 'No se pudo asignar el template',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setAssigningId(null);
    }
  };

  const renderCalendarDays = () => {
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <GridItem key={`empty-${i}`} p={2} minH="100px" bg="gray.900" borderRadius="md" />
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = formatDateKey(day);
      const dayData = calendarData[dateKey];
      const isToday = dateKey === moment().format('YYYY-MM-DD');
      const hasQuestions = dayData && dayData.questions_count > 0;

      days.push(
        <GridItem
          key={day}
          p={2}
          minH="100px"
          bg={isToday ? 'purple.900' : 'gray.800'}
          borderRadius="md"
          border={isToday ? '2px solid' : '1px solid'}
          borderColor={isToday ? 'purple.500' : 'gray.700'}
          cursor="pointer"
          _hover={{ bg: isToday ? 'purple.800' : 'gray.700' }}
          onClick={() => handleDayClick(day)}
          position="relative"
        >
          <Text
            fontSize="sm"
            fontWeight={isToday ? 'bold' : 'normal'}
            color={isToday ? 'purple.200' : 'white'}
          >
            {day}
          </Text>

          {hasQuestions && (
            <VStack mt={2} spacing={1} align="start">
              <HStack>
                <Badge colorScheme="blue" fontSize="xs">
                  {dayData.questions_count} {dayData.questions_count === 1 ? 'pregunta' : 'preguntas'}
                </Badge>
              </HStack>
              <HStack>
                <Badge colorScheme="green" fontSize="xs">
                  {dayData.total_points} pts
                </Badge>
              </HStack>
              {dayData.has_speed_bonus && (
                <Tooltip label="Tiene bonus por velocidad">
                  <Badge colorScheme="yellow" fontSize="xs">
                    <FaBolt style={{ display: 'inline', marginRight: '4px' }} />
                    Speed
                  </Badge>
                </Tooltip>
              )}
              {!dayData.has_active_questions && (
                <Badge colorScheme="red" fontSize="xs">
                  Inactivas
                </Badge>
              )}
            </VStack>
          )}

          {!hasQuestions && (
            <Text fontSize="xs" color="gray.500" mt={2}>
              Sin preguntas
            </Text>
          )}
        </GridItem>
      );
    }

    return days;
  };

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full' px={4}>
        <Heading textAlign="center" mb={6}>
          Trivia - Calendario
        </Heading>

        <Flex w='full' alignItems='center' gap='5' mb={6}>
          <Button
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={handleCreateQuestion}
            isDisabled={missingPermission(OlaggModule.Trivia, PermissionEnum.CREATE)}
          >
            Crear Pregunta
          </Button>
        </Flex>

        {/* Calendar Navigation */}
        <Flex justify="space-between" align="center" mb={4}>
          <IconButton
            aria-label="Mes anterior"
            icon={<FaChevronLeft />}
            onClick={handlePrevMonth}
            variant="ghost"
            colorScheme="whiteAlpha"
          />
          <Heading size="md">
            {MONTHS[month]} {year}
          </Heading>
          <IconButton
            aria-label="Mes siguiente"
            icon={<FaChevronRight />}
            onClick={handleNextMonth}
            variant="ghost"
            colorScheme="whiteAlpha"
          />
        </Flex>

        {loading ? (
          <Center h="400px">
            <Spinner size="xl" />
          </Center>
        ) : (
          <>
            {/* Days of week header */}
            <Grid templateColumns="repeat(7, 1fr)" gap={2} mb={2}>
              {DAYS_OF_WEEK.map((day) => (
                <GridItem key={day} textAlign="center">
                  <Text fontWeight="bold" color="gray.400" fontSize="sm">
                    {day}
                  </Text>
                </GridItem>
              ))}
            </Grid>

            {/* Calendar grid */}
            <Grid templateColumns="repeat(7, 1fr)" gap={2}>
              {renderCalendarDays()}
            </Grid>
          </>
        )}

        {/* Modal for day assignments */}
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>
              Preguntas del {selectedDate && moment(selectedDate).format('DD/MM/YYYY')}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {loadingQuestions ? (
                <Center py={8}>
                  <Spinner />
                </Center>
              ) : (
                <>
                  {/* Asignaciones existentes */}
                  {selectedDayAssignments.length > 0 && (
                    <VStack spacing={4} align="stretch" mb={6}>
                      <Text fontWeight="bold" fontSize="sm" color="gray.300">
                        Preguntas asignadas ({selectedDayAssignments.length})
                      </Text>
                      {selectedDayAssignments.map((assignment) => (
                        <Box
                          key={assignment.id}
                          p={3}
                          bg="gray.700"
                          borderRadius="md"
                        >
                          <HStack justify="space-between" mb={2}>
                            <HStack>
                              <Badge colorScheme="purple">#{assignment.position}</Badge>
                              <Badge colorScheme={assignment.active ? 'green' : 'red'}>
                                {assignment.active ? <FaCheck /> : <FaTimes />}
                              </Badge>
                            </HStack>
                            <HStack>
                              <Button
                                size="xs"
                                colorScheme="blue"
                                onClick={() => handleEditTemplate(assignment.template_id)}
                              >
                                Editar
                              </Button>
                              <IconButton
                                aria-label="Eliminar asignación"
                                icon={<FaTrash />}
                                size="xs"
                                colorScheme="red"
                                onClick={() => handleDeleteAssignment(assignment.id)}
                                isLoading={deletingId === assignment.id}
                                isDisabled={missingPermission(OlaggModule.Trivia, PermissionEnum.DELETE)}
                              />
                            </HStack>
                          </HStack>
                          <Text noOfLines={1} fontSize="sm">{assignment.title}</Text>
                          <Text fontSize="xs" color="gray.400">{assignment.points} pts</Text>
                        </Box>
                      ))}
                    </VStack>
                  )}

                  {/* Sección para agregar templates */}
                  <Divider my={4} />
                  <VStack spacing={3} align="stretch">
                    <Text fontWeight="bold" fontSize="sm" color="gray.300">
                      Agregar template existente
                    </Text>
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none">
                        <FaSearch color="gray" />
                      </InputLeftElement>
                      <Input
                        placeholder="Buscar templates..."
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        bg="gray.700"
                        borderColor="gray.600"
                        _placeholder={{ color: 'gray.400' }}
                      />
                    </InputGroup>

                    {loadingTemplates ? (
                      <Center py={4}>
                        <Spinner size="sm" />
                      </Center>
                    ) : availableTemplates.length > 0 ? (
                      <VStack spacing={2} align="stretch" maxH="250px" overflowY="auto">
                        {availableTemplates.map((template) => (
                          <Box
                            key={template.uid}
                            p={2}
                            bg="gray.700"
                            borderRadius="md"
                            _hover={{ bg: 'gray.600' }}
                          >
                            <HStack justify="space-between" mb={2}>
                              <Box flex={1} minW={0}>
                                <Text fontSize="sm" color="gray.400">ID: {template.id}</Text>
                                <Text fontSize="sm" noOfLines={1}>{template.title}</Text>
                                <Text fontSize="xs" color="gray.400">{template.points} pts</Text>
                                <Text fontSize="xs" color="gray.400">{template.usageCount} asignaciones</Text>
                              </Box>
                            </HStack>
                            <HStack justify="space-between">
                              <HStack spacing={2}>
                                <Text fontSize="xs" color="gray.400">Pos:</Text>
                                <Input
                                  type="number"
                                  size="xs"
                                  w="60px"
                                  min={1}
                                  defaultValue={selectedDayAssignments.length + 1}
                                  bg="gray.600"
                                  borderColor="gray.500"
                                  onChange={(e) => setSelectedPosition(parseInt(e.target.value) || null)}
                                />
                              </HStack>
                              <Button
                                size="xs"
                                colorScheme="green"
                                leftIcon={<FaPlus />}
                                onClick={() => handleAssignTemplate(template.uid, selectedPosition ?? selectedDayAssignments.length + 1)}
                                isLoading={assigningId === template.uid}
                                isDisabled={missingPermission(OlaggModule.Trivia, PermissionEnum.CREATE)}
                              >
                                Asignar
                              </Button>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    ) : (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={2}>
                        No se encontraron templates
                      </Text>
                    )}

                    <Divider my={2} />
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="purple"
                      leftIcon={<FaPlus />}
                      onClick={handleCreateQuestion}
                      isDisabled={missingPermission(OlaggModule.Trivia, PermissionEnum.CREATE)}
                    >
                      Crear nuevo template
                    </Button>
                  </VStack>
                </>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </Center>
  );
};

export default TriviaCalendarView;
